import React from 'react';
import clsx from 'clsx';
import { ApolloError } from '@apollo/client';
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { messageFromError } from '@oysterjs/core/graphql/errors';
import { Button } from '@oysterjs/ui/button';
import { Description, Field, FieldGroup, Fieldset, Label, Legend } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Text } from '@oysterjs/ui/text';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Spinner } from '@oysterjs/ui/spinner';
import { Textarea } from '@oysterjs/ui/textarea';

import { useGeneratePresetQuestionsMutation } from '~/api/graphql';
import {
  type ConvertToPresetWizardAction,
  type ConvertToPresetWizardState,
  type WizardClarifyingQuestion,
  assembleGeneratePresetQuestionsInput
} from '~/components/workspaces/lib/convert-to-preset-wizard.utils';

const GENERIC_QUESTIONS_ERROR = 'Could not generate clarifying questions.';

// Mirrors maxPresetQaTextRunes in pkg/service/api/api/preset_generation.go: the
// task definition ships as a Q&A answer, and exceeding it fails
// startPresetGeneration at step 5, after every input is entered.
const MAX_QA_TEXT_CHARS = 2000;

/**
 * The question on screen: its 2-4 options one per line plus a free-text fallback
 * (both write the one stored answer string — an option is selected when it equals
 * the answer, and the free-text field holds the answer only when it is not one of
 * the options). Leaving it blank is how a user declines it; an unanswered question
 * is simply not sent.
 */
const Question = ({
  question,
  answer,
  onAnswer
}: {
  question: WizardClarifyingQuestion;
  answer: string;
  onAnswer: (answer: string) => void;
}) => {
  const customText = question.options.includes(answer) ? '' : answer;

  return (
    // Field spaces label -> control; it has no control -> control rule, so the
    // free-text input's gap is declared in the same idiom rather than hand-set.
    <Field className="[&>[data-slot=control]+[data-slot=control]]:mt-2">
      <Label>{question.question}</Label>
      <RadioGroup
        value={question.options.includes(answer) ? answer : undefined}
        onChange={onAnswer}
        aria-label={question.question}
      >
        {/* The list sits inside the group, not on it: RadioGroup merges its own
            space-y, which would offset every row after the first. */}
        <div className="grid gap-1">
          {question.options.map((option) => (
            <RadioField
              key={option}
              className={clsx(
                'rounded-lg px-3 py-2.5 transition-colors duration-150',
                answer === option
                  ? 'bg-sky-50 dark:bg-sky-500/10'
                  : 'hover:bg-neutral-50 dark:hover:bg-white/5'
              )}
            >
              <Radio color="sky" value={option} />
              <Label>{option}</Label>
            </RadioField>
          ))}
        </div>
      </RadioGroup>
      {question.allowFreeText && (
        <Input
          value={customText}
          maxLength={MAX_QA_TEXT_CHARS}
          placeholder="Or type your own answer"
          onChange={(e) => onAnswer(e.currentTarget.value)}
        />
      )}
    </Field>
  );
};

// Walks the question list. Labelled rather than bare chevrons: the step shows one
// question at a time, so this row is the only cue that more follow.
const QuestionPager = ({
  index,
  total,
  onPrev,
  onNext
}: {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) => (
  <div className="flex items-center justify-between gap-3">
    <Button type="button" plain onClick={onPrev} disabled={index === 0}>
      <ChevronLeftIcon className="size-4" aria-hidden />
      Previous
    </Button>
    <span className="tabular-nums text-sm/6 text-neutral-500 dark:text-neutral-400">
      Question {index + 1} of {total}
    </span>
    <Button type="button" color="white" onClick={onNext} disabled={index === total - 1}>
      Next question
      <ChevronRightIcon className="size-4" aria-hidden />
    </Button>
  </div>
);

/**
 * Step 4: the required task definition plus the AI-generated clarifying
 * questions. The task definition blocks advancing (see canProceedFromStep); the
 * questions are best-effort and fully optional — individually skippable and
 * skippable as a whole. Questions are fetched on first arrival and cached in
 * the reducer so answers persist across back-nav; editing a question-shaping
 * input (workspaces, base preset, templates) clears the cache, and revisiting
 * this step refetches.
 */
export const ConvertToPresetQuestionsStep = ({
  state,
  dispatch,
  active
}: {
  state: ConvertToPresetWizardState;
  dispatch: React.Dispatch<ConvertToPresetWizardAction>;
  active: boolean;
}) => {
  const [generateQuestions] = useGeneratePresetQuestionsMutation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Read the latest state at fetch time without churning the callback identity
  // on every keystroke.
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const fetchQuestions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const input = assembleGeneratePresetQuestionsInput(stateRef.current);
    const inputSnapshot = JSON.stringify(input);
    let refetching = false;
    try {
      const res = await generateQuestions({ variables: { input } });
      // The question-shaping inputs may have changed while this (LLM-slow) call
      // was in flight — back-nav edits clear the reducer's cache, and a stale
      // result must not repopulate it as if fresh. Refetch with the current
      // inputs instead of applying.
      if (
        JSON.stringify(assembleGeneratePresetQuestionsInput(stateRef.current)) !== inputSnapshot
      ) {
        refetching = true;
        void fetchQuestions();
        return;
      }
      const questions = res.data?.generatePresetQuestions;
      if (questions) {
        dispatch({ type: 'setAiQuestions', questions });
      } else {
        // errorPolicy 'all' lands server errors here rather than rejecting, and
        // res.errors[0].message is the raw diagnostic channel — filter as below.
        setError(
          messageFromError(new ApolloError({ graphQLErrors: res.errors }), GENERIC_QUESTIONS_ERROR)
        );
      }
    } catch (e) {
      setError(messageFromError(e, GENERIC_QUESTIONS_ERROR));
    } finally {
      // The recursive refetch owns the loading state from here on.
      if (!refetching) {
        setLoading(false);
      }
    }
  }, [generateQuestions, dispatch]);

  // Fetch whenever the user is on step 4 with nothing cached. `requestedRef`
  // keeps the error case from refetching in a loop (retry stays explicit) and
  // strict-mode double-effects from firing twice; it re-arms when questions
  // land in the cache, so a later cache clear (question-shaping inputs changed
  // via back-nav — see the reducer's withAiQuestionsCleared) fetches afresh.
  const questionsCached = state.aiQuestions !== null;
  const requestedRef = React.useRef(false);

  React.useEffect(() => {
    if (questionsCached) {
      requestedRef.current = false;
    }
  }, [questionsCached]);

  React.useEffect(() => {
    if (active && !questionsCached && !requestedRef.current) {
      requestedRef.current = true;
      void fetchQuestions();
    }
  }, [active, questionsCached, fetchQuestions]);

  const retryQuestions = () => {
    requestedRef.current = true;
    void fetchQuestions();
  };

  const questions = state.aiQuestions ?? [];

  // One question on screen at a time, walked with the pager. Clamped rather than
  // reset so a refetch that returns fewer questions cannot land out of range.
  const [index, setIndex] = React.useState(0);
  const current = Math.min(index, Math.max(questions.length - 1, 0));

  return (
    <div className="space-y-6">
      <Field>
        <Label>Define the task this preset codifies</Label>
        <Description>
          In your own words, what should Cara do every time this preset runs? Be specific about the
          goal and the deliverable — this anchors everything Cara generates.
        </Description>
        <Textarea
          value={state.taskDefinition}
          rows={3}
          maxLength={MAX_QA_TEXT_CHARS}
          onChange={(e) => dispatch({ type: 'setTaskDefinition', value: e.currentTarget.value })}
          placeholder="e.g. Compare the client's expiring policy to each renewal quote and flag every coverage or limit that got worse."
        />
      </Field>

      <Fieldset>
        {/* Legend alone keeps text-base on desktop while Label/Text drop to
            text-sm/6, which made this heading larger than the one above it. */}
        <Legend className="sm:text-sm/6">A few questions to tailor the preset</Legend>
        <Text>
          Optional. Cara drafted these from the workspaces you picked. Answer what&apos;s useful —
          anything you leave blank is left out.
        </Text>

        {/* FieldGroup carries data-slot="control", so Fieldset supplies the gap
            above it and the spacing between fields. */}
        <FieldGroup>
          {loading && (
            <div className="flex items-center gap-2 text-sm/6 text-neutral-500 dark:text-neutral-400">
              <Spinner size="size-4" color="neutral" />
              Generating clarifying questions…
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm/6 text-neutral-500 dark:text-neutral-400">{error}</span>
              <button
                type="button"
                onClick={retryQuestions}
                className="inline-flex items-center gap-1 text-sm/6 text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 transition-colors duration-150"
              >
                <ArrowPathIcon className="size-4" aria-hidden />
                Try again
              </button>
            </div>
          )}
          {!loading && !error && questions.length === 0 && (
            <Text>No additional questions — you&apos;re all set.</Text>
          )}
          {!loading && questions.length > 0 && (
            <Question
              key={questions[current].id}
              question={questions[current]}
              answer={state.questionAnswers[questions[current].id] ?? ''}
              onAnswer={(answer) =>
                dispatch({
                  type: 'setQuestionAnswer',
                  questionId: questions[current].id,
                  answer
                })
              }
            />
          )}
          {!loading && questions.length > 1 && (
            <QuestionPager
              index={current}
              total={questions.length}
              onPrev={() => setIndex(current - 1)}
              onNext={() => setIndex(current + 1)}
            />
          )}
        </FieldGroup>
      </Fieldset>
    </div>
  );
};
